import { markdownUnderlineSource } from '@Pages/pageRuntime/lib/markdownUnderline';

// Strip Markdown syntax for a single plain-text reading. Each construct is stripped with its
// own pattern rather than through a full parser, so a Markdown field cell stays as light as the
// public page bundle that renders it (see tiptap.ts for the same tradeoff on Content fields).
// Code spans and backslash escapes are lifted out first so no later pattern reads inside them.

const PROTECT_OPEN = '';
const PROTECT_CLOSE = '';
const PROTECTED_PATTERN = new RegExp(`${PROTECT_OPEN}(\\d+)${PROTECT_CLOSE}`, 'g');

const CODE_FENCE_PATTERN =
  /^ {0,3}(`{3,}|~{3,})[^\n]*\n([\s\S]*?)(?:\n {0,3}\1[`~]*[ \t]*(?=\n|$)|(?![\s\S]))/gm;
const INLINE_CODE_PATTERN = /(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)/g;
const ESCAPE_PATTERN = /\\([!-/:-@[-`{-~])/g;
const HARD_BREAK_PATTERN = /\\$/gm;
const REFERENCE_DEFINITION_PATTERN = /^ {0,3}\[([^\]\n]+)\]:[ \t]*\S.*$/gm;
// A bare destination may hold balanced parentheses, such as `/wiki/Foo_(bar)`. A regex cannot
// recurse, so this reads two levels of nesting, which is deeper than any real URL. An angle
// destination and a quoted title are separate tokens, so a parenthesis inside them needs no match.
const BARE_DESTINATION = String.raw`(?:[^\s()]|\((?:[^\s()]|\([^\s()]*\))*\))+`;
const LINK_TITLE = String.raw`"[^"]*"|'[^']*'|\([^()]*\)`;
const DESTINATION = String.raw`\(\s*(?:(?:<[^<>\n]*>|${BARE_DESTINATION})(?:\s+(?:${LINK_TITLE}))?\s*)?\)`;
const IMAGE_PATTERN = new RegExp(String.raw`!\[[^\]]*\]${DESTINATION}`, 'g');
const REFERENCE_IMAGE_PATTERN = /!\[([^\]]*)\]\[([^\]]*)\]/g;
const LINK_PATTERN = new RegExp(String.raw`\[([^\]]*)\]${DESTINATION}`, 'g');
const REFERENCE_LINK_PATTERN = /\[([^\]]+)\]\[([^\]]*)\]/g;
const AUTOLINK_PATTERN = /<((?:https?|mailto):[^>\s]+)>/g;
// Only a line that opens with the heading marker is a heading, so a `#` that ends an ordinary
// paragraph line stays. The optional closing run of `#` is part of the heading syntax, and the
// callback removes it so that the match itself stays linear.
const HEADING_PATTERN = /^ {0,3}#{1,6}(?:[ \t]+(.*))?$/gm;
const HEADING_CLOSING_HASHES_PATTERN = /(?:^|[ \t])#+$/;
const HEADING_UNDERLINE_PATTERN = /^ {0,3}=+[ \t]*$/gm;
const THEMATIC_BREAK_PATTERN = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm;
const BLOCKQUOTE_PATTERN = /^[ \t]*(?:>[ \t]?)+/gm;
const LIST_MARKER_PATTERN = /^[ \t]*(?:[-*+]|\d+[.)])[ \t]+(?:\[[ xX]\][ \t]+)?/gm;
const TABLE_CELL_SEPARATOR = ', ';
// A delimiter row that opens with a pipe may be indented by at most three spaces.
const TABLE_DELIMITER_INDENT_PATTERN = /^(?: {0,3}\||[ \t]*[^|\s])/;
const TABLE_DELIMITER_CELL_PATTERN = /^:?-+:?$/;
// A table body row needs a pipe, so a line without one ends the table. A line that opens another
// block ends the table too, even when it holds a pipe.
const TABLE_BLOCK_START_PATTERN =
  /^ {0,3}(?:#{1,6}(?:[ \t]|$)|>|(?:[-*+]|\d{1,9}[.)])(?:[ \t]|$)|([-*_])(?:[ \t]*\1){2,}[ \t]*$)/;
const UNDERLINE_PATTERN = new RegExp(markdownUnderlineSource, 'g');
// Longest delimiter first, so `**bold**` is not read as two unmatched `*` pairs.
const STAR_EMPHASIS_PATTERN = /(\*\*\*|\*\*|\*|~~)(?!\s)([^\n]*?[^\s])\1/g;
// An underscore inside a word is literal (`snake_case_name`), so `_` pairs need a non-word
// character on the outside.
const UNDERSCORE_EMPHASIS_PATTERN =
  /(^|[^A-Za-z0-9])(___|__|_)(?!\s)([^\n]*?[^\s])\2(?![A-Za-z0-9])/g;

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' '
};
const ENTITY_PATTERN = /&(?:amp|lt|gt|quot|nbsp|#39);/g;

function restoreProtected(text: string, protectedText: readonly string[]): string {
  return text.replace(
    PROTECTED_PATTERN,
    (_match, index: string) => protectedText[Number(index)] ?? ''
  );
}

// An escaped character in a label, such as `[re\]f]`, is protected under a different token at
// each use, so labels compare after the protected text returns.
function referenceLabel(label: string, protectedText: readonly string[]): string {
  return restoreProtected(label, protectedText).trim().replace(/\s+/g, ' ').toLowerCase();
}

// A reference resolves only when a definition names its label. An unresolved `[first][last]`
// is literal text in Markdown, so it stays as written.
function stripReferences(
  text: string,
  labels: ReadonlySet<string>,
  protectedText: readonly string[]
): string {
  const resolves = (label: string, fallback: string): boolean =>
    labels.has(referenceLabel(label === '' ? fallback : label, protectedText));

  return text
    .replace(REFERENCE_IMAGE_PATTERN, (match, alt: string, label: string) =>
      resolves(label, alt) ? '' : match
    )
    .replace(REFERENCE_LINK_PATTERN, (match, linkText: string, label: string) =>
      resolves(label, linkText) ? linkText : match
    );
}

function tableCells(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function tableRowText(row: string): string {
  return tableCells(row)
    .filter((cell) => cell !== '')
    .join(TABLE_CELL_SEPARATOR);
}

function isTableDelimiterRow(row: string, columns: number): boolean {
  if (!row.includes('|') || !TABLE_DELIMITER_INDENT_PATTERN.test(row)) {
    return false;
  }

  const cells = tableCells(row);

  return cells.length === columns && cells.every((cell) => TABLE_DELIMITER_CELL_PATTERN.test(cell));
}

// A table keeps its cell text, one row for each line, and drops the pipes and the delimiter row.
function stripTables(text: string): string {
  const lines = text.split('\n');
  const output: string[] = [];

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';
    const delimiter = lines[index + 1];

    if (
      !line.includes('|') ||
      delimiter === undefined ||
      !isTableDelimiterRow(delimiter, tableCells(line).length)
    ) {
      output.push(line);
      continue;
    }

    output.push(tableRowText(line));
    index += 2;

    for (; index < lines.length; index++) {
      const row = lines[index] ?? '';

      if (!row.includes('|') || TABLE_BLOCK_START_PATTERN.test(row)) {
        break;
      }

      output.push(tableRowText(row));
    }

    index--;
  }

  return output.join('\n');
}

function stripEmphasis(text: string): string {
  let current = text;

  for (;;) {
    const next = current
      .replace(STAR_EMPHASIS_PATTERN, '$2')
      .replace(UNDERSCORE_EMPHASIS_PATTERN, '$1$3');

    if (next === current) {
      return current;
    }

    current = next;
  }
}

export function markdownPlainText(markdown: string): string {
  const protectedText: string[] = [];
  const protect = (text: string): string => {
    protectedText.push(text);
    return `${PROTECT_OPEN}${protectedText.length - 1}${PROTECT_CLOSE}`;
  };
  const definedLabels = new Set<string>();

  const withoutDefinitions = markdown
    .replace(/\r\n?/g, '\n')
    .replaceAll(PROTECT_OPEN, '')
    .replaceAll(PROTECT_CLOSE, '')
    // The blank lines around a fence end any table that the fence follows.
    .replace(CODE_FENCE_PATTERN, (_match, _fence, code: string) => `\n\n${protect(code)}\n\n`)
    .replace(INLINE_CODE_PATTERN, (_match, _ticks, code: string) => protect(code))
    .replace(ESCAPE_PATTERN, (_match, character: string) => protect(character))
    .replace(HARD_BREAK_PATTERN, '')
    .replace(REFERENCE_DEFINITION_PATTERN, (_match, label: string) => {
      definedLabels.add(referenceLabel(label, protectedText));

      return '';
    });

  const stripped = stripEmphasis(
    stripTables(stripReferences(withoutDefinitions, definedLabels, protectedText))
      .replace(IMAGE_PATTERN, '')
      .replace(LINK_PATTERN, '$1')
      .replace(AUTOLINK_PATTERN, '$1')
      .replace(HEADING_PATTERN, (_match, content: string | undefined) =>
        (content ?? '').trimEnd().replace(HEADING_CLOSING_HASHES_PATTERN, '').trimEnd()
      )
      .replace(HEADING_UNDERLINE_PATTERN, '')
      .replace(THEMATIC_BREAK_PATTERN, '')
      .replace(BLOCKQUOTE_PATTERN, '')
      .replace(LIST_MARKER_PATTERN, '')
      .replace(UNDERLINE_PATTERN, '$1')
  );

  return restoreProtected(
    stripped
      .replace(ENTITY_PATTERN, (entity) => ENTITIES[entity] ?? entity)
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\n{2,}/g, '\n'),
    protectedText
  ).trim();
}
